import { ChangeEvent, FormEvent, useState } from "react";
import { Form } from "react-bootstrap";
import TextField from "@components/FormsV2/TextField/TextField";
import MultipleLineFieldV2 from "@components/FormsV2/MultipleLineFieldV2/MultipleLineFieldV2";
import { isHttpUrl } from "../constants/url";
import type { ChangeOrderFormValues } from "../types";
import CauseRadios from "../builder/CauseRadios";
import { amountError } from "../builder/useChangeOrderDraft";

export interface ChangeOrderFormProps {
  id: string;
  initialValues: ChangeOrderFormValues;
  readOnly?: boolean;
  childProjectId?: string | null;
  onSubmit: (values: ChangeOrderFormValues) => void;
}

interface Errors {
  cause?: string;
  externalUrl?: string;
  hours?: string;
  revenue?: string;
}

export const validate = (values: ChangeOrderFormValues): Errors => {
  const errors: Errors = {};
  if (!values.cause) errors.cause = "Select a cause";
  const url = values.externalUrl.trim();
  if (url && !isHttpUrl(url)) errors.externalUrl = "Enter an http(s) URL";
  // Optional on Edit (legacy reference-only rows may be blank); a value given
  // must be zero or more.
  if (values.referenceOnly) {
    const optional = (value: string) => (value.trim() === "" ? undefined : amountError(value, ""));
    const hours = optional(values.hours);
    const revenue = optional(values.revenue);
    if (hours) errors.hours = hours;
    if (revenue) errors.revenue = revenue;
  }
  return errors;
};

export const ChangeOrderForm = ({
  id,
  initialValues,
  readOnly = false,
  childProjectId,
  onSubmit,
}: ChangeOrderFormProps): JSX.Element => {
  const [values, setValues] = useState<ChangeOrderFormValues>(initialValues);
  const [errors, setErrors] = useState<Errors>({});

  const set = <K extends keyof ChangeOrderFormValues>(
    key: K,
    v: ChangeOrderFormValues[K]
  ) => {
    setValues((prev) => ({ ...prev, [key]: v }));
    if (key in errors) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (readOnly) return;
    const found = validate(values);
    setErrors(found);
    if (Object.values(found).some(Boolean)) return;
    onSubmit({ ...values, externalUrl: values.externalUrl.trim() });
  };

  return (
    <Form id={id} data-testid={id} onSubmit={handleSubmit} noValidate>
      <Form.Group className="mb-3">
        <Form.Label>Type</Form.Label>
        <div>
          {values.referenceOnly ? (
            "Reference only"
          ) : (
            <>
              Authored
              {childProjectId && (
                <>
                  {" — "}
                  <a href={`/projects/${childProjectId}/project_editor`}>
                    open the change-request project
                  </a>
                </>
              )}
            </>
          )}
        </div>
      </Form.Group>

      <CauseRadios
        id={id}
        value={values.cause}
        error={errors.cause}
        disabled={readOnly}
        onChange={(c) => set("cause", c)}
      />

      <div className="row">
        <div className="col-sm-12">
          <Form.Label htmlFor={`${id}-cause-note`}>Note</Form.Label>
          <MultipleLineFieldV2
            label={null}
            id={`${id}-cause-note`}
            rows={3}
            value={values.causeNote}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set("causeNote", e.target.value)
            }
            disabled={readOnly}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-sm-6">
          <Form.Label htmlFor={`${id}-external-reference`}>
            External Reference
          </Form.Label>
          <TextField
            label={null}
            id={`${id}-external-reference`}
            value={values.externalReference}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set("externalReference", e.target.value)
            }
            disabled={readOnly}
          />
        </div>
        <div className="col-sm-6">
          <Form.Label htmlFor={`${id}-external-url`}>External URL</Form.Label>
          <TextField
            label={null}
            id={`${id}-external-url`}
            value={values.externalUrl}
            placeholder="https://"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set("externalUrl", e.target.value)
            }
            disabled={readOnly}
          />
          {errors.externalUrl && (
            <Form.Text className="text-danger">{errors.externalUrl}</Form.Text>
          )}
        </div>
      </div>

      {values.referenceOnly && (
        <div className="row">
          {(["hours", "revenue"] as const).map((key) => (
            <div className="col-sm-6" key={key}>
              <Form.Label htmlFor={`${id}-${key}`}>
                {key === "hours" ? "Hours" : "Revenue"}
              </Form.Label>
              <Form.Control
                id={`${id}-${key}`}
                type="number"
                min={0}
                step="any"
                value={values[key]}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  set(key, e.target.value)
                }
                disabled={readOnly}
              />
              {errors[key] && (
                <Form.Text className="text-danger">{errors[key]}</Form.Text>
              )}
            </div>
          ))}
        </div>
      )}
    </Form>
  );
};

export default ChangeOrderForm;
